import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { BASE_END_POINT_INTERVIEW, INTERVIEW_END_POINT } from '../../../config';

/**
 * Redux slice for managing interviews state
 */

// Initial state
const initialState = {
  interviews: [],
  selectedInterview: null,
  loading: false,
  error: null,
  fetched: false,
  creating: false
};

/**
 * Fetch all interviews for a company
 */
export const fetchInterviews = createAsyncThunk(
  'interviews/fetchInterviews',
  async ({ companyId, startDate = null, endDate = null }, { rejectWithValue }) => {
    try {
      const localAuth = getLocalStorageAuthentication();
      let url = `${BASE_END_POINT_INTERVIEW}${INTERVIEW_END_POINT}/company/${companyId}`;
      
      if (startDate && endDate) {
        url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
      }

      console.log('🔍 [Interviews] Fetching interviews:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch interviews: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Interviews] Fetched:', data.length, 'interviews');
      return data;
    } catch (error) {
      console.error('❌ [Interviews] Error fetching interviews:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create a new interview
 */
export const createInterview = createAsyncThunk(
  'interviews/createInterview',
  async (interviewData, { rejectWithValue }) => {
    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INTERVIEW}${INTERVIEW_END_POINT}`;

      console.log('📅 [Interviews] Creating interview:', url);
      console.log('📦 [Interviews] Data:', interviewData);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(interviewData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create interview: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Interviews] Interview created:', data.id);
      return data;
    } catch (error) {
      console.error('❌ [Interviews] Error creating interview:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Get a specific interview by ID
 */
export const fetchInterviewById = createAsyncThunk(
  'interviews/fetchInterviewById',
  async (interviewId, { rejectWithValue }) => {
    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INTERVIEW}${INTERVIEW_END_POINT}/${interviewId}`;

      console.log('🔍 [Interviews] Fetching interview:', interviewId);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch interview: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Interviews] Interview fetched:', data.id);
      return data;
    } catch (error) {
      console.error('❌ [Interviews] Error fetching interview:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update interview status
 */
export const updateInterviewStatus = createAsyncThunk(
  'interviews/updateInterviewStatus',
  async ({ interviewId, status }, { rejectWithValue }) => {
    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INTERVIEW}${INTERVIEW_END_POINT}/${interviewId}/status`;

      console.log('🔄 [Interviews] Updating status:', interviewId, 'to', status);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`Failed to update interview status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Interviews] Status updated:', data.id);
      return data;
    } catch (error) {
      console.error('❌ [Interviews] Error updating status:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Create the slice
const interviewsSlice = createSlice({
  name: 'interviews',
  initialState,
  reducers: {
    selectInterview: (state, action) => {
      state.selectedInterview = action.payload;
    },
    clearSelectedInterview: (state) => {
      state.selectedInterview = null;
    },
    clearInterviews: (state) => {
      state.interviews = [];
      state.fetched = false;
    }
  },
  extraReducers: (builder) => {
    // Fetch interviews
    builder
      .addCase(fetchInterviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviews.fulfilled, (state, action) => {
        state.loading = false;
        state.interviews = action.payload;
        state.fetched = true;
      })
      .addCase(fetchInterviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create interview
    builder
      .addCase(createInterview.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createInterview.fulfilled, (state, action) => {
        state.creating = false;
        state.interviews.unshift(action.payload);
      })
      .addCase(createInterview.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      });

    // Fetch interview by ID
    builder
      .addCase(fetchInterviewById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviewById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedInterview = action.payload;
      })
      .addCase(fetchInterviewById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update interview status
    builder
      .addCase(updateInterviewStatus.fulfilled, (state, action) => {
        const index = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );
        if (index !== -1) {
          state.interviews[index] = action.payload;
        }
        if (state.selectedInterview?.id === action.payload.id) {
          state.selectedInterview = action.payload;
        }
      });
  }
});

// Export actions
export const { selectInterview, clearSelectedInterview, clearInterviews } =
  interviewsSlice.actions;

// Selectors
export const interviewsSelector = (state) => state.interviews.interviews;
export const selectedInterviewSelector = (state) => state.interviews.selectedInterview;
export const interviewsLoadingSelector = (state) => state.interviews.loading;
export const interviewsErrorSelector = (state) => state.interviews.error;
export const interviewsFetchedSelector = (state) => state.interviews.fetched;
export const interviewsCreatingSelector = (state) => state.interviews.creating;

// Export reducer
export default interviewsSlice.reducer;

